import { Button } from "@svea/design-system-components-react";
import { ButtonGroup, Dialog, normalizeSsn, useText } from "@sveabank/cc-common";
import { useState } from "react";
import { useLazyFetchPersonInfoQuery } from "../../../services/application";
import type {
	GuarantorInteractive,
	GuarantorLookup,
	GuarantorName,
} from "../../../types/guarantor";
import GuarantorConfirmInfo from "./guarantor-confirm-info";
import GuarantorLookupForm from "./guarantor-lookup-form";

type Props = {
	isModalOpen: boolean;
	setOpen: (isOpen: boolean) => void;
	handleAdd: (guarantor: GuarantorInteractive) => void;
};

const AddGuarantorModal = ({ isModalOpen, setOpen, handleAdd }: Props) => {
	const [t, texts] = useText();

	const [hasSubmittedForm, setHasSubmittedForm] = useState(false);
	const [guarantorSsn, setGuarantorSsn] = useState("");

	const [fetchPersonInfo, { data: personInfo, isFetching }] = useLazyFetchPersonInfoQuery();

	const handleGetName = async (values: GuarantorLookup) => {
		const normalizedSsn = normalizeSsn(values.ssn);
		await fetchPersonInfo(normalizedSsn).unwrap();
		setHasSubmittedForm(true);
		setGuarantorSsn(normalizedSsn);
	};

	const onRemove = () => {
		setHasSubmittedForm(false);
		setGuarantorSsn("");
	};

	const handleClose = () => {
		setHasSubmittedForm(false);
		setOpen(false);
	};

	const handleAddGuarantor = (info: GuarantorName) => {
		handleAdd({
			names: info,
			checked: false,
			email: null,
			guarantorType: "external",
			nationalId: guarantorSsn,
		});
		handleClose();
	};

	return (
		<Dialog
			isOpen={isModalOpen}
			setOpen={setOpen}
			center={false}
			heading={t(texts.signPage.addGuarantorModal.title)}
		>
			<Dialog.Content>
				{hasSubmittedForm && personInfo ? (
					<GuarantorConfirmInfo
						name={personInfo}
						onRemove={onRemove}
						ssn={guarantorSsn}
						descriptionText={t(texts.signPage.addGuarantorModal.description2)}
					/>
				) : (
					<GuarantorLookupForm
						handleGetName={handleGetName}
						descriptionText={t(texts.signPage.addGuarantorModal.description1)}
						labelText={t(texts.signPage.addGuarantorModal.ssnLabel)}
						buttonText={t(texts.signPage.addGuarantorModal.getNameButton)}
					/>
				)}
			</Dialog.Content>
			<ButtonGroup row>
				{personInfo && (
					<Button
						width="full"
						variant="primary"
						type="button"
						onPress={() => {
							handleAddGuarantor(personInfo);
						}}
						isPending={isFetching}
					>
						{t(texts.signPage.addGuarantorModal.addButton)}
					</Button>
				)}
				<Button variant="secondary" type="button" width="full" onPress={handleClose}>
					{t(texts.signPage.addGuarantorModal.closeButton)}
				</Button>
			</ButtonGroup>
		</Dialog>
	);
};

export default AddGuarantorModal;
